import { Injectable } from '@angular/core';
import { Firestore, collection, doc, documentId, query, where } from '@angular/fire/firestore';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import * as fromDoubtsActions from '../actions/doubts.actions';

import { Observable, of } from 'rxjs';
import { map, catchError, switchMap, take } from 'rxjs/operators';
import { FirebaseService } from '../../services/firebase/firebase.service';
import { Doubt, DoubtFirebaseResponse } from '../../models/doubt.model';
import { User } from '../../models/user.model';

@Injectable()
export class DoubtsEffects {
  private readonly collectionName = 'doubts';
  constructor(
    private readonly action$: Actions,
    private readonly firestore: Firestore,
    private readonly firebaseService: FirebaseService,
  ) {}

  // Load Doubts
  loadDoubts$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromDoubtsActions.LoadDoubtsAction),
      switchMap(
        ({
            user
        }: {
          type: string;
          user: User;
        }) =>
          this.getDoubtsForUser(user).pipe(
            switchMap((doubts) => this.addUsersToDoubts(doubts, user)),
            map((doubts) => ({
              type: fromDoubtsActions.LOAD_DOUBTS_SUCCESS,
              data: doubts
            })),
            catchError((error) =>
              of({ type: fromDoubtsActions.LOAD_DOUBTS_FAIL, error })
            )
          )
      )
    )
  );

  // Add Comment
  addComment$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromDoubtsActions.AddCommentAction),
      switchMap(async ({ comment, doubt }) => {
        const doubtToEdit: Doubt = JSON.parse(
          JSON.stringify({
            comments: [...(doubt.comments || []), comment]
          })
        );
        doubtToEdit.comments = doubtToEdit.comments?.map((comment) => {
          delete comment.user;
          return { ...comment, date: new Date(comment.date) };
        });
        delete doubtToEdit.id;
        await this.firebaseService.updateDoc(
          this.collectionName,
          doubt.id!,
          doubtToEdit
        );
        return {
          comment: {
            ...comment,
            user: comment.userId === doubt.user?.id ? doubt.user : doubt.trainer
          },
          doubt
        };
      }),
      map(({ comment, doubt }) => {
        return {
          type: fromDoubtsActions.ADD_COMMENT_SUCCESS,
          comment,
          doubt
        };
      }),
      catchError((error) =>
        of({ type: fromDoubtsActions.ADD_COMMENT_FAIL, error: error })
      )
    )
  );

  // Add Doubt
  addDoubt$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromDoubtsActions.AddDoubtAction),
      switchMap(async ({ doubt, user }) => {
        const trainerRef = doc(this.firestore, `users/${user.trainerId}`);
        const userRef = doc(this.firestore, `users/${user.id}`);

        const doubtToAdd = { ...doubt, trainerRef, userRef };
        const resp = await this.firebaseService.addDoc(
          this.collectionName,
          doubtToAdd
        );
        return { ...doubt, id: resp.id, user, trainerRef };
      }),
      switchMap((doubt: Doubt) => {
        const ref = query(
          collection(this.firestore, 'users'),
          where(documentId(), '==', doubt.trainerRef!.id)
        );
        delete doubt.trainerRef;
        return this.firebaseService.getDataOfCollectionByField(ref).pipe(
          take(1), map((docs) => ({ ...doubt, trainer: docs[0] })))
      }),
      map((doubt) => {
        return {
          type: fromDoubtsActions.ADD_DOUBT_SUCCESS,
          data: doubt
        };
      }),
      catchError((error) =>
        of({ type: fromDoubtsActions.ADD_DOUBT_FAIL, error: error })
      )
    )
  );

  private getDoubtsForUser(
    user: User
  ): Observable<Doubt[]> {
    const userRef = doc(this.firestore, `users/${user.id}`);
    
    const ref = query(
        collection(this.firestore, 'doubts'),
        where('userRef', '==', userRef)
    );

    return this.firebaseService.getDataOfCollectionByField(ref).pipe(take(1), map((resp) => resp.map((doc) => ({
        ...doc as DoubtFirebaseResponse
    }))));
  }

  private addUsersToDoubts(
    doubts: Doubt[],
    user: User
  ): Observable<Doubt[]> {
    if (doubts?.length > 0) {
      const trainerRef = doubts[0].trainerRef;
      const ref = query(
        collection(this.firestore, 'users'),
        where(documentId(), '==', trainerRef!.id)
      );
      return this.firebaseService.getDataOfCollectionByField(ref).pipe(
        take(1),
        map((docs) =>
          doubts.map((doubt) => {
            const trainer = docs[0];
            const comments = doubt.comments?.map((comment) => ({
              ...comment,
              user: user.id === comment.userId ? user : trainer,
              date: new Date((comment.date as unknown as { seconds: number }).seconds * 1000)
            }));
            doubt.date = new Date(
              (doubt.date as unknown as { seconds: number }).seconds
            );

            delete doubt.trainerRef;
            delete doubt.userRef;
            delete user.trainerRef;
            delete user.teamRef;

            return {
              ...doubt,
              trainer,
              user,
              comments
            } as Doubt;
          })
        )
      );
    }
    return of(doubts);
  }
}
